/**
 * Keeps `--app-height` equal to the visible viewport, and marks the document
 * while the on-screen keyboard is up.
 *
 * `100dvh` tracks the browser chrome but not the on-screen keyboard: iOS
 * leaves the layout viewport alone and scrolls the page instead, which pushes
 * the header off the top and hides the composer. Sizing the shell to the
 * visual viewport keeps both in view with the keyboard up, and scrolling the
 * window back to the origin undoes the push Safari applies before this fires.
 *
 * The keyboard flag exists because iOS keeps reporting the home-indicator
 * inset (`env(safe-area-inset-bottom)`) while the keyboard covers it, which
 * would leave an empty band between the composer and the keys; the shell
 * drops that inset while `data-keyboard` is set (see index.css).
 */
export function trackViewportHeight(): void {
  const viewport = window.visualViewport
  if (!viewport) return

  const root = document.documentElement

  const apply = () => {
    const height = Math.round(viewport.height)
    root.style.setProperty("--app-height", `${height}px`)

    // Browser chrome collapsing changes the height by tens of pixels; a
    // keyboard takes a third of the screen. The gap between them is wide,
    // so a fixed threshold tells them apart on every phone.
    const covered = window.innerHeight - height
    root.toggleAttribute("data-keyboard", covered > 120)

    if (window.scrollY !== 0) window.scrollTo(0, 0)
  }

  viewport.addEventListener("resize", apply)
  viewport.addEventListener("scroll", apply)
  apply()
}
